import { useCallback, useEffect, useMemo, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import MapPicker from "../../../components/base/MapPicker";
import AddressAutocomplete from "../../../components/base/AddressAutocomplete";
import { buildThemeClasses } from "../theme";
import {
  searchProspects,
  listProspects,
  convertProspects,
  discardProspects,
  cleanupLegacyLeads,
  type Prospect,
  type ConvertResult,
} from "../services/prospectingService";
import { LEAD_PRODUCTS } from "../services/leadsService";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Prospección — LIVE section (Automatizaciones → Prospección).
// Finds commerce leads on the web (Google Places) for a keyword/category in an
// area, stages them for review, and converts the selected ones into leads.
// Converted leads are flagged so the onLeadCreated trigger SKIPS the auto-call.
// ===========================================================================

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

// Default search center (Mexico City) used until the user picks a point.
const DEFAULT_CENTER = { lat: 19.4326, lng: -99.1332 };
const DEFAULT_RADIUS_KM = 5;
const DEFAULT_MAX_RESULTS = 60;
const HARD_MAX_RESULTS = 120;

type AreaMode = "place" | "map";

function phoneOf(p: Prospect): string | null {
  return p.whatsapp || p.phone || null;
}

/** "State, Country" label for a prospect, or null when neither is known. */
function locationOf(p: Prospect): string | null {
  const parts = [p.state, p.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

export default function ProspectingLive() {
  const tc = buildThemeClasses();

  // ---- Search form state ---------------------------------------------------
  const [keyword, setKeyword] = useState("");
  const [product, setProduct] = useState<string>(LEAD_PRODUCTS[0]);
  const [areaMode, setAreaMode] = useState<AreaMode>("place");
  const [placeText, setPlaceText] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [maxResults, setMaxResults] = useState(DEFAULT_MAX_RESULTS);
  const [searching, setSearching] = useState(false);

  // ---- Results / review state ---------------------------------------------
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState(false);
  const [lastConvert, setLastConvert] = useState<ConvertResult | null>(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await listProspects({ status: "pending", limit: 200 });
      setProspects(page.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar los prospectos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // ---- Search --------------------------------------------------------------
  const canSearch =
    keyword.trim().length > 0 &&
    (areaMode === "map"
      ? Boolean(coords)
      : placeText.trim().length > 0 || Boolean(coords));

  const handleSearch = async () => {
    if (!canSearch || searching) return;
    setSearching(true);
    setLastConvert(null);
    try {
      // In either mode we may already have coordinates (map pin, or an
      // autocomplete selection); otherwise the backend geocodes `placeText`.
      const useCoords = coords;
      const result = await searchProspects({
        keyword: keyword.trim(),
        product,
        ...(useCoords ? { lat: useCoords.lat, lng: useCoords.lng } : { place: placeText.trim() }),
        radius: radiusKm,
        maxResults,
      });
      await loadPending();
      setSelected(new Set());
      Swal.fire({
        icon: result.saved > 0 ? "success" : "info",
        title: result.saved > 0 ? `${result.saved} prospecto(s) nuevos` : "Sin resultados nuevos",
        text:
          result.saved > 0
            ? `Encontrados ${result.total} · ${result.duplicates} ya estaban en la lista`
            : `La búsqueda no arrojó prospectos nuevos (${result.duplicates} duplicados).`,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo buscar",
        text: err.response?.data?.message || err.message || "Error al ejecutar la búsqueda",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setSearching(false);
    }
  };

  // ---- Selection -----------------------------------------------------------
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = prospects.length > 0 && selected.size === prospects.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(prospects.map((p) => p.id)));

  const selectedIds = useMemo(() => [...selected], [selected]);

  // ---- Convert / discard ---------------------------------------------------
  const handleConvert = async () => {
    if (!selectedIds.length || working) return;
    const confirm = await Swal.fire({
      icon: "question",
      title: `Convertir ${selectedIds.length} prospecto(s) en empresas`,
      html:
        `Se crearán como empresas (aunque no tengan teléfono).<br/>` +
        `Se vuelven leads cuando agregues una persona en <b>Empresas</b>.`,
      showCancelButton: true,
      confirmButtonText: "Convertir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
    });
    if (!confirm.isConfirmed) return;

    setWorking(true);
    try {
      const result = await convertProspects(selectedIds);
      setLastConvert(result);
      setSelected(new Set());
      await loadPending();
      Swal.fire({
        icon: result.created > 0 ? "success" : "warning",
        title: `${result.created} empresa(s) creadas`,
        text: result.skipped > 0 ? `${result.skipped} omitidas` : undefined,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo convertir",
        text: err.response?.data?.message || err.message || "Error al crear las empresas",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setWorking(false);
    }
  };

  // ---- One-time cleanup of legacy prospecting leads ------------------------
  const handleCleanup = async () => {
    if (working) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Limpiar leads heredados",
      html:
        `Quita los leads creados por la versión anterior de Prospección ` +
        `(origen “prospecting”) y los deja solo como empresas.`,
      showCancelButton: true,
      confirmButtonText: "Limpiar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!confirm.isConfirmed) return;

    setWorking(true);
    try {
      const result = await cleanupLegacyLeads();
      Swal.fire({
        icon: "success",
        title: `${result.removed} lead(s) heredados removidos`,
        text: `${result.companies} empresa(s) conservadas`,
        confirmButtonColor: brand.primary,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo limpiar",
        text: err.response?.data?.message || err.message || "Error en la limpieza",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setWorking(false);
    }
  };

  const handleDiscard = async () => {
    if (!selectedIds.length || working) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: `Descartar ${selectedIds.length} prospecto(s)`,
      text: "Se quitarán de la lista de revisión.",
      showCancelButton: true,
      confirmButtonText: "Descartar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!confirm.isConfirmed) return;

    setWorking(true);
    try {
      await discardProspects(selectedIds);
      setSelected(new Set());
      await loadPending();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "No se pudieron descartar",
        confirmButtonColor: brand.primary,
      });
    } finally {
      setWorking(false);
    }
  };

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-search-eye-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Prospección web</h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              Encuentra comercios por categoría y zona, revísalos y conviértelos en empresas
            </p>
          </div>
        </div>

        {/* Search form */}
        <div className={`mb-6 rounded-xl border p-5 ${tc.border} ${tc.cardBg}`}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Keyword */}
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                Categoría o término de búsqueda
              </label>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="restaurantes, ferreterías, dentistas…"
                className={inputCls}
              />
            </div>

            {/* Max results + radius */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                  Máx. de leads
                </label>
                <input
                  type="number"
                  min={1}
                  max={HARD_MAX_RESULTS}
                  value={maxResults}
                  onChange={(e) =>
                    setMaxResults(
                      Math.max(1, Math.min(HARD_MAX_RESULTS, Number(e.target.value) || 1)),
                    )
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
                  Radio: {radiusKm} km
                </label>
                <input
                  type="range"
                  min={0.5}
                  max={50}
                  step={0.5}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  className="mt-2 w-full accent-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Product — which product these companies are prospects for */}
          <div className="mt-4">
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>
              Producto de interés
            </label>
            <div className="flex flex-wrap gap-2">
              {LEAD_PRODUCTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProduct(p)}
                  className={`rounded-lg border px-3 py-1.5 text-sm transition ${
                    product === p
                      ? "border-primary-500 bg-primary-500 text-white"
                      : `${tc.border} ${tc.cardBg} ${tc.textSecondary} hover:border-primary-500`
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Area picker */}
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <span className={`text-xs font-medium ${tc.textSecondary}`}>Zona:</span>
              <div className={`inline-flex overflow-hidden rounded-lg border ${tc.border}`}>
                <button
                  type="button"
                  onClick={() => setAreaMode("place")}
                  className={`px-3 py-1 text-xs ${areaMode === "place" ? "bg-primary-500 text-white" : `${tc.cardBg} ${tc.textSecondary}`}`}
                >
                  <i className="ri-map-pin-2-line mr-1" />Lugar
                </button>
                <button
                  type="button"
                  onClick={() => setAreaMode("map")}
                  className={`px-3 py-1 text-xs ${areaMode === "map" ? "bg-primary-500 text-white" : `${tc.cardBg} ${tc.textSecondary}`}`}
                >
                  <i className="ri-road-map-line mr-1" />Mapa
                </button>
              </div>
            </div>

            {MAPS_KEY ? (
              <APIProvider apiKey={MAPS_KEY} libraries={["places"]}>
                {areaMode === "place" ? (
                  <AddressAutocomplete
                    placeholder="Ciudad, colonia o dirección (ej. Guadalajara, Jalisco)"
                    className={inputCls}
                    value={placeText}
                    onAddressTextChange={(text) => {
                      setPlaceText(text);
                      setCoords(null); // typed text → let the backend geocode
                    }}
                    onAddressSelect={(address, lat, lng) => {
                      setPlaceText(address);
                      setCoords({ lat, lng });
                      setCenter({ lat, lng });
                    }}
                  />
                ) : (
                  <div className="h-[360px] overflow-hidden rounded-lg">
                    <MapPicker
                      lat={(coords ?? center).lat}
                      lng={(coords ?? center).lng}
                      radius={radiusKm * 1000}
                      shape="circular"
                      onLocationSelect={(lat, lng) => {
                        setCoords({ lat, lng });
                        setCenter({ lat, lng });
                      }}
                      onRadiusChange={(meters) =>
                        setRadiusKm(Math.max(0.5, Math.min(50, Math.round((meters / 1000) * 2) / 2)))
                      }
                    />
                  </div>
                )}
              </APIProvider>
            ) : (
              // No Maps JS key → fall back to a plain place text input; the
              // backend geocodes it. (Map-pin mode needs VITE_GOOGLE_MAPS_API_KEY.)
              <input
                value={placeText}
                onChange={(e) => {
                  setPlaceText(e.target.value);
                  setCoords(null);
                }}
                placeholder="Ciudad, colonia o dirección (ej. Guadalajara, Jalisco)"
                className={inputCls}
              />
            )}
            {areaMode === "map" && coords && (
              <p className={`mt-1 text-xs ${tc.textSecondary}`}>
                Centro: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · radio {radiusKm} km
              </p>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleSearch}
              disabled={!canSearch || searching}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className={searching ? "ri-loader-4-line animate-spin" : "ri-search-line"} />
              {searching ? "Buscando…" : "Buscar prospectos"}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className={`text-sm font-semibold ${tc.text}`}>
            Prospectos por revisar
            <span className={`ml-2 font-normal ${tc.textSecondary}`}>({prospects.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${tc.textSecondary}`}>{selected.size} seleccionados</span>
            <button
              type="button"
              onClick={handleCleanup}
              disabled={working}
              title="Quita los leads creados por la versión anterior, dejándolos solo como empresas"
              className={`rounded-lg border px-3 py-1.5 text-sm ${tc.border} ${tc.textSecondary} disabled:opacity-50`}
            >
              <i className="ri-eraser-line mr-1" />Limpiar leads heredados
            </button>
            <button
              type="button"
              onClick={handleDiscard}
              disabled={!selected.size || working}
              className={`rounded-lg border px-3 py-1.5 text-sm ${tc.border} ${tc.text} disabled:opacity-50`}
            >
              <i className="ri-delete-bin-line mr-1" />Descartar
            </button>
            <button
              type="button"
              onClick={handleConvert}
              disabled={!selected.size || working}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-600 disabled:opacity-50"
            >
              <i className={working ? "ri-loader-4-line animate-spin" : "ri-building-line"} />
              Convertir en empresas
            </button>
          </div>
        </div>

        {/* Last conversion summary */}
        {lastConvert && (
          <div className={`mb-3 rounded-lg border p-3 text-sm ${tc.border} ${tc.cardBg}`}>
            <span className="text-emerald-500">{lastConvert.created} empresas creadas</span>
            {lastConvert.skipped > 0 && (
              <span className="ml-3 text-amber-500">{lastConvert.skipped} omitidas</span>
            )}
            {lastConvert.errors.length > 0 && (
              <ul className={`mt-2 list-disc pl-5 text-xs ${tc.textSecondary}`}>
                {lastConvert.errors.slice(0, 8).map((e, i) => (
                  <li key={i}>{e.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Results table */}
        <div className={`overflow-hidden rounded-xl border ${tc.border} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-1 animate-spin" />Cargando…
            </div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-red-500">{error}</div>
          ) : prospects.length === 0 ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              No hay prospectos por revisar. Ejecuta una búsqueda para empezar.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className={`border-b ${tc.border} ${tc.textSecondary}`}>
                  <tr>
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-primary-500" />
                    </th>
                    <th className="px-3 py-2 font-medium">Negocio</th>
                    <th className="px-3 py-2 font-medium">Teléfono</th>
                    <th className="px-3 py-2 font-medium">Dirección</th>
                    <th className="px-3 py-2 font-medium">Estado / País</th>
                    <th className="px-3 py-2 font-medium">Rating</th>
                    <th className="px-3 py-2 font-medium">Sitio</th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((p) => {
                    const phone = phoneOf(p);
                    const location = locationOf(p);
                    const isSel = selected.has(p.id);
                    return (
                      <tr
                        key={p.id}
                        className={`border-b ${tc.border} ${isSel ? "bg-primary-500/5" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={isSel} onChange={() => toggle(p.id)} className="accent-primary-500" />
                        </td>
                        <td className="px-3 py-2">
                          <div className={`font-medium ${tc.text}`}>{p.name || "—"}</div>
                          {p.category && (
                            <div className={`text-xs capitalize ${tc.textSecondary}`}>{p.category}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {phone ? (
                            <span className={tc.text}>{phone}</span>
                          ) : (
                            <span className="text-xs text-amber-500">sin teléfono</span>
                          )}
                        </td>
                        <td className={`max-w-xs px-3 py-2 ${tc.textSecondary}`}>
                          <span className="line-clamp-2">{p.address || "—"}</span>
                        </td>
                        <td className={`px-3 py-2 ${tc.textSecondary}`}>
                          {location ? <span className={tc.text}>{location}</span> : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {p.rating != null ? (
                            <span className={tc.text}>
                              <i className="ri-star-fill mr-0.5 text-amber-400" />
                              {p.rating}
                              {p.ratingsCount != null && (
                                <span className={`ml-1 text-xs ${tc.textSecondary}`}>({p.ratingsCount})</span>
                              )}
                            </span>
                          ) : (
                            <span className={tc.textSecondary}>—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {p.website ? (
                            <a
                              href={p.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-500 hover:underline"
                            >
                              <i className="ri-external-link-line" />
                            </a>
                          ) : (
                            <span className={tc.textSecondary}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
